import { CONNECT_PAGE_PATHS } from "@vidimo/shared/contracts";
import { env } from "@/lib/env";

/**
 * The connect pages are served from an origin of their own
 * (connect.<env>.vidimo.app), the same bundle as the web app on
 * my.<env>.vidimo.app, so the third-party script the WhatsApp page loads
 * shares no storage with the signed-in app (ADR-0137). These helpers keep each
 * page on its own origin; with neither variable set (local dev, native and
 * desktop bundles) everything stays on the one origin the page is on.
 */

/** The env's URL as an origin; the env schema has already checked it parses. */
const originOf = (url: string | undefined) =>
  url ? new URL(url).origin : null;

export function isConnectPagePath(pathname: string): boolean {
  return Object.values(CONNECT_PAGE_PATHS).some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

/** Where `path` lives in the web app: on the app origin when one is set. */
export function webAppUrl(path: string): string {
  const appOrigin = originOf(env.VITE_APP_URL);
  return appOrigin ? `${appOrigin}${path}` : path;
}

/**
 * Where a page on the wrong origin belongs, or null when it is where it
 * should be: a connect page opened on the app origin moves to the connect
 * origin, and anything else opened on the connect origin (a bookmark, a typed
 * address) moves to the app origin. Nothing moves unless both origins are set.
 */
export function misplacedPageUrl(location: Location): string | null {
  const appOrigin = originOf(env.VITE_APP_URL);
  const connectOrigin = originOf(env.VITE_CONNECT_URL);
  if (!(appOrigin && connectOrigin)) {
    return null;
  }
  const { hash, origin, pathname, search } = location;
  const isConnectPage = isConnectPagePath(pathname);
  if (isConnectPage && origin === appOrigin) {
    return `${connectOrigin}${pathname}${search}${hash}`;
  }
  if (!isConnectPage && origin === connectOrigin) {
    return `${appOrigin}${pathname}${search}${hash}`;
  }
  return null;
}
